import { useEffect, useRef } from 'react';
import { POS_PERMISSIONS, type OnlineOrderAlertRow } from '@matix/types';
import { useAuth } from '@matix/auth';
import { onlineOrdersAPI } from '@matix/api-client';
import { useLang } from '../../lib/language';
import { useCanI } from '../../lib/permissions';
import { fmt } from '../../lib/mock-data';
import { useModuleOn } from '../../store/tenantConfig';
import { useUI } from '../../store/ui';
import { unseenOnlineOrders, useOnlineOrders } from '../../store/onlineOrders';
import { useReprintReceipt } from '../print/useReprintReceipt';

/**
 * The cashier's "new web order" alert.
 *
 * - `OnlineOrdersPoller` asks the server every 20 s (and when the till regains
 *   focus) for the branch's web orders of the last 24 hours.
 * - A web order this till has not acknowledged pops up here with a chime —
 *   one at a time, oldest first — with Print, Delivery Manager (for a delivery,
 *   when the cashier may open it) and OK. A cancellation from the shop pops up
 *   the same way, in red.
 * - The top-bar bell counts what is still unacknowledged and opens the list of
 *   the last 24 hours (`OnlineOrdersPanel`).
 * - "Print automatically on this till" (in that panel) makes THIS till print
 *   each new web order once, by itself — see the store for the rules.
 */

const POLL_MS = 20_000;

/**
 * The poll's clock runs in a tiny Web Worker: Chrome throttles a page's own
 * timers hard when the till window is minimised or behind another (khwada:
 * web orders printed minutes late). A worker's timer is not throttled that way.
 * Falls back to window.setInterval where workers are unavailable.
 */
function startTicker(ms: number, onTick: () => void): () => void {
  try {
    const src = `setInterval(() => postMessage(0), ${ms});`;
    const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
    const w = new Worker(url);
    w.onmessage = () => onTick();
    return () => { w.terminate(); URL.revokeObjectURL(url); };
  } catch {
    const id = window.setInterval(onTick, ms);
    return () => window.clearInterval(id);
  }
}

export function OnlineOrdersPoller(): null {
  const { cashier } = useAuth();
  const attach = useOnlineOrders((s) => s.attach);
  const refresh = useOnlineOrders((s) => s.refresh);
  const nextToPrint = useOnlineOrders((s) => s.printQueue[0]);
  const shiftPrintQueue = useOnlineOrders((s) => s.shiftPrintQueue);
  const acknowledgeReceipt = useOnlineOrders((s) => s.acknowledgeReceipt);
  const reprint = useReprintReceipt();
  const printing = useRef(false);

  // Auto-print: one receipt at a time through the till's normal reprint path
  // (straight to the default printer on a kiosk-printing till).
  useEffect(() => {
    if (nextToPrint == null || printing.current) return;
    printing.current = true;
    const id = nextToPrint;
    const extId = useOnlineOrders.getState().rows.find((r) => r.receipt_id === id)?.id;
    // Claim it first: with 2-4 tills open at a shop each used to print it
    // (khwada). Only the till the server says claimed:true prints; another till
    // that lost the claim treats it as handled — no print, no pop-up. If the
    // claim call itself fails, print: a second copy beats a missed order.
    const claim = extId == null ? Promise.resolve(true) : onlineOrdersAPI.claimPrint(extId).catch(() => true);
    void claim
      .then((mine) => (mine ? reprint(id) : true))
      // Printed (or printed elsewhere) → no pop-up. Acknowledge BEFORE leaving
      // the queue, so the alert never flashes in between. Failed → it pops up.
      .then((ok) => { if (ok) acknowledgeReceipt(id); })
      .finally(() => {
        printing.current = false;
        shiftPrintQueue();
      });
  }, [nextToPrint, reprint, shiftPrintQueue, acknowledgeReceipt]);
  const tenant = cashier?.tenant_db ?? null;
  const branch = cashier?.branch_id ?? null;

  useEffect(() => {
    if (!tenant || branch == null) return undefined;
    attach(`pos:cashier:web-orders-seen:${tenant}:${branch}`);
    void refresh();
    const stop = startTicker(POLL_MS, () => void refresh());
    const onFocus = () => void refresh();
    // Back in front after being hidden / asleep: check at once, not in 20 s.
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tenant, branch, attach, refresh]);

  return null;
}

function OrderFacts({ row }: { row: OnlineOrderAlertRow }): JSX.Element {
  const { t } = useLang();
  const paid = row.payment_state === 'paid';
  return (
    <div style={{ display: 'grid', gap: 6, fontSize: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <strong style={{ fontSize: 22 }}>#{row.entry_no ?? row.receipt_id}</strong>
        <span style={{ fontWeight: 700, fontSize: 18 }}>{row.total != null ? fmt(row.total) : '—'}</span>
      </div>
      <div style={{ color: 'var(--text-muted)' }}>
        {row.channel_name}
        {row.external_order_number ? ` · ${t({ en: 'shop order', ar: 'طلب المتجر' })} ${row.external_order_number}` : ''}
      </div>
      {(row.customer_name || row.customer_phone) && (
        <div>
          {row.customer_name ?? ''} {row.customer_phone ? <span dir="ltr">{row.customer_phone}</span> : null}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            background: paid ? 'var(--success-soft, #DCFCE7)' : 'var(--warning-soft, #FEF3C7)',
            color: paid ? 'var(--success, #166534)' : 'var(--warning, #92400E)',
          }}
        >
          {paid
            ? t({ en: 'Paid online — nothing to collect', ar: 'مدفوع إلكترونياً — لا تحصيل' })
            : t({ en: 'Not paid — collect on delivery', ar: 'غير مدفوع — يُحصَّل عند التسليم' })}
        </span>
        {row.order_type === 2 && (
          <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: 'var(--surface-2, #EEF2F7)' }}>
            {t({ en: 'Delivery', ar: 'توصيل' })}
          </span>
        )}
      </div>
    </div>
  );
}

/** The pop-up for the oldest web order this till has not acknowledged. */
export function OnlineOrderAlert(): JSX.Element | null {
  const { t } = useLang();
  const rows = useOnlineOrders((s) => s.rows);
  const seen = useOnlineOrders((s) => s.seen);
  const printQueue = useOnlineOrders((s) => s.printQueue);
  const panelOpen = useOnlineOrders((s) => s.panelOpen);
  const acknowledge = useOnlineOrders((s) => s.acknowledge);
  const setTab = useUI((s) => s.setTab);
  const reprint = useReprintReceipt();
  const hasDeliveryPermission = useCanI(POS_PERMISSIONS.DeliveryManager);
  const deliveryOn = useModuleOn('delivery');
  const canDeliveryManager = hasDeliveryPermission && deliveryOn;

  const unseen = unseenOnlineOrders({ rows, seen, printQueue });
  const row = unseen[0];
  if (!row || panelOpen) return null;
  const cancelled = row.status === 'cancelled';

  return (
    <div className="receipt-view-backdrop" style={{ zIndex: 1250 }}>
      <div
        className="void-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={t({ en: 'New web order', ar: 'طلب جديد من المتجر' })}
        style={{ borderTop: `6px solid ${cancelled ? 'var(--danger, #B91C1C)' : 'var(--primary, #1D4ED8)'}` }}
      >
        <header className="void-dialog-header">
          <h2 style={{ color: cancelled ? 'var(--danger, #B91C1C)' : undefined }}>
            {cancelled
              ? t({ en: 'Web order CANCELLED by the shop', ar: 'أُلغي طلب من المتجر' })
              : t({ en: 'New web order', ar: 'طلب جديد من المتجر' })}
            {unseen.length > 1 ? ` (1 / ${unseen.length})` : ''}
          </h2>
        </header>
        <div className="void-dialog-body">
          <OrderFacts row={row} />
          {cancelled && (
            <p style={{ margin: '10px 0 0', fontSize: 13 }}>
              {t({
                en: 'Stop preparing it. The invoice was NOT reversed — a manager decides whether to void it or make a return.',
                ar: 'أوقف تجهيزه. لم تُعكس الفاتورة — يقرر المسؤول إلغاءها أو عمل مرتجع.',
              })}
            </p>
          )}
        </div>
        <footer className="void-dialog-actions" style={{ flexWrap: 'wrap' }}>
          {!cancelled && (
            <button type="button" className="receipts-btn receipts-btn-secondary" onClick={() => void reprint(row.receipt_id)}>
              {t({ en: 'Print', ar: 'طباعة' })}
            </button>
          )}
          {!cancelled && row.order_type === 2 && canDeliveryManager && (
            <button
              type="button"
              className="receipts-btn receipts-btn-secondary"
              onClick={() => {
                acknowledge(row);
                setTab('deliveryManager');
              }}
            >
              {t({ en: 'Delivery Manager', ar: 'مدير التوصيل' })}
            </button>
          )}
          <button type="button" className="receipts-btn receipts-btn-primary" onClick={() => acknowledge(row)}>
            {t({ en: 'OK', ar: 'تم' })}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** The bell's list: every web order of the last 24 hours at this branch. */
export function OnlineOrdersPanel(): JSX.Element | null {
  const { t } = useLang();
  const rows = useOnlineOrders((s) => s.rows);
  const seen = useOnlineOrders((s) => s.seen);
  const open = useOnlineOrders((s) => s.panelOpen);
  const setOpen = useOnlineOrders((s) => s.setPanelOpen);
  const acknowledgeAll = useOnlineOrders((s) => s.acknowledgeAll);
  const autoPrint = useOnlineOrders((s) => s.autoPrint);
  const setAutoPrint = useOnlineOrders((s) => s.setAutoPrint);
  const reprint = useReprintReceipt();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;
  const isNew = (r: OnlineOrderAlertRow) => !seen.has(`${r.id}:${r.status}`);

  return (
    <div
      className="receipt-view-backdrop"
      style={{ zIndex: 1240 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="void-dialog" role="dialog" aria-modal="true" style={{ maxWidth: 560 }}>
        <header className="void-dialog-header">
          <h2>{t({ en: 'Web orders — last 24 hours', ar: 'طلبات المتجر — آخر 24 ساعة' })}</h2>
        </header>
        <label
          style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '0 20px 10px', fontSize: 13, cursor: 'pointer' }}
        >
          <input type="checkbox" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            <strong>{t({ en: 'Print new web orders automatically on this till', ar: 'طباعة طلبات المتجر الجديدة تلقائياً على هذا الجهاز' })}</strong>
            <br />
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {t({
                en: "Turn it on at ONE till only. Each new order prints once, to this computer's default printer.",
                ar: 'فعّلها على جهاز واحد فقط. يُطبع كل طلب جديد مرة واحدة على الطابعة الافتراضية لهذا الجهاز.',
              })}
            </span>
          </span>
        </label>
        <div className="void-dialog-body" style={{ maxHeight: '60vh', overflowY: 'auto', display: 'grid', gap: 10 }}>
          {rows.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>
              {t({ en: 'No web orders at this branch in the last 24 hours.', ar: 'لا توجد طلبات من المتجر لهذا الفرع خلال آخر 24 ساعة.' })}
            </p>
          ) : (
            rows.map((r) => (
              <div
                key={`${r.id}:${r.status}`}
                style={{
                  border: '1px solid var(--border)',
                  borderInlineStart: `4px solid ${r.status === 'cancelled' ? 'var(--danger, #B91C1C)' : isNew(r) ? 'var(--primary, #1D4ED8)' : 'var(--border)'}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                }}
              >
                <OrderFacts row={r} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', fontSize: 12 }}>
                  {r.status === 'cancelled' && (
                    <strong style={{ color: 'var(--danger, #B91C1C)' }}>{t({ en: 'Cancelled by the shop', ar: 'ألغاه المتجر' })}</strong>
                  )}
                  <span style={{ color: 'var(--text-muted)', marginInlineEnd: 'auto' }}>
                    {r.at ? new Date(r.at).toLocaleString() : ''}
                  </span>
                  {r.status !== 'cancelled' && (
                    <button type="button" className="receipts-btn receipts-btn-secondary" onClick={() => void reprint(r.receipt_id)}>
                      {t({ en: 'Print', ar: 'طباعة' })}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <footer className="void-dialog-actions">
          {rows.some(isNew) && (
            <button type="button" className="receipts-btn receipts-btn-secondary" onClick={acknowledgeAll}>
              {t({ en: 'Mark all as seen', ar: 'تعليم الكل كمقروء' })}
            </button>
          )}
          <button type="button" className="receipts-btn receipts-btn-primary" onClick={() => setOpen(false)}>
            {t({ en: 'Close', ar: 'إغلاق' })}
          </button>
        </footer>
      </div>
    </div>
  );
}
